import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import Seal from '../components/Seal';
import InstallCard from '../components/InstallCard';
import { useI18n, t as tr, optLabel } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { fmtMoney, fmtLongDate, statusTag } from '../lib/format';
import { ledgerTotals, paidTotal, balanceOf } from '../lib/ledger';
import LedgerCard from '../components/LedgerCard';
import CompliancePanel, { ComplianceBanner } from '../components/CompliancePanel';
import MessageButton from '../components/MessageButton';
import TechSettlement from '../components/TechSettlement';
import RecordList from '../components/RecordList';
import { localized, isTranslated, sourceLangName } from '../lib/translated';
import DisplayOptions from '../components/DisplayOptions';
import { useDisplayOptions } from '../lib/displayOptions';
import {
  IconJobs, IconEstimates, IconInvoices, IconPayments, IconMore, IconChevron,
} from '../components/PortalIcons';

// Expertise taxonomy: main category → sub-services. Every category and skill
// from the original Google Form is preserved (so past applications still read
// correctly), organized into a category tree with a few added sub-services.
// VALUES stay in English (that's what saves to the database) — labels translate.
export const TAXONOMY = [
  { cat: 'Plumbing', subs: ['General Plumbing Repairs', 'Fixtures & Faucets', 'Water Heaters'] },
  { cat: 'Electrical', subs: ['General Electrical Repairs', 'Lighting & Ceiling Fans', 'Outlets & Switches'] },
  { cat: 'Carpentry', subs: ['Deck Building', 'Railings', 'Kitchen Cabinet Builder', 'Trim & Finish Carpentry'] },
  { cat: 'Interior and Exterior Painting', subs: ['Interior Painting', 'Exterior Painting', 'Cabinet Painting'] },
  { cat: 'General Repairs', subs: ['Drywall Repair', 'Window/Door Repair', 'Carpet Repair and Installation', 'Attic and wall insulation installation'] },
  { cat: 'Flooring & Tile', subs: ['Flooring Installation', 'Tile Installation', 'Backsplash installation', 'Granite countertops installation'] },
  { cat: 'Remodeling & Construction', subs: ['Kitchen and Bathroom Remodeling', 'Basement Finish'] },
  { cat: 'Exterior & Yard', subs: ['Landscaping', 'Roofing Repair', 'Concrete'] },
  { cat: 'HVAC', subs: ['Furnace & A/C', 'Ductwork & Vents'] },
  { cat: 'Demolition and Junk Removal', subs: ['Demolition', 'Junk Hauling'] },
];

const YesNo = ({ label, required, value, onChange }) => (
  <div className="form-group">
    <label>{label} {required && <span className="req">*</span>}</label>
    <div style={{ display: 'flex', gap: 8 }}>
      {['Yes', 'No'].map(opt => (
        <button key={opt} type="button" className={`btn btn-sm ${value === opt ? 'btn-primary' : 'btn-outline'}`} onClick={() => onChange(opt)}>{optLabel(opt)}</button>
      ))}
    </div>
  </div>
);

// Partnership application shown until the tech completes it (once per account).
function VendorApplication() {
  const { vendorAppForm, setVendorAppForm, vendorDocFiles, setVendorDocFiles, isSubmittingApp, handleSubmitVendorApp } = useApp();
  const { t } = useI18n();
  const f = vendorAppForm;
  const set = (patch) => setVendorAppForm(p => ({ ...p, ...patch }));

  // Category tree: adding a category shows its sub-service checkboxes;
  // removing it also unchecks its sub-services.
  const addCategory = (cat) => {
    if (cat && !f.expertise.includes(cat)) set({ expertise: [...f.expertise, cat] });
  };
  const removeCategory = (cat) => {
    const subs = TAXONOMY.find(x => x.cat === cat)?.subs || [];
    set({
      expertise: f.expertise.filter(c => c !== cat),
      skills: f.skills.filter(s => !subs.includes(s)),
    });
  };
  const toggleSkill = (s) => set({ skills: f.skills.includes(s) ? f.skills.filter(x => x !== s) : [...f.skills, s] });

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
      <div className="auth-card" style={{ maxWidth: 560 }}>
        <div className="auth-title">{t('app.title')}</div>
        <div className="auth-sub">{t('app.sub')}</div>

        <div className="form-group"><label>{t('app.address')} <span className="req">*</span></label><input type="text" value={f.address} onChange={e => set({ address: e.target.value })} placeholder={t('app.addressPh')} /></div>
        <div className="form-group"><label>{t('app.years')} <span className="req">*</span></label><input type="number" min="0" value={f.yearsExperience} onChange={e => set({ yearsExperience: e.target.value })} placeholder={t('app.yearsPh')} /></div>

        <div className="form-group">
          <label>{t('app.expertiseQ')} <span className="req">*</span></label>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{t('app.subsHint')}</div>
          <select value="" onChange={e => addCategory(e.target.value)} style={{ marginBottom: 10 }}>
            <option value="">{t('app.addCategory')}</option>
            {TAXONOMY.filter(x => !f.expertise.includes(x.cat)).map(x => (
              <option key={x.cat} value={x.cat}>{optLabel(x.cat)}</option>
            ))}
          </select>
          {f.expertise.map(cat => {
            const subs = TAXONOMY.find(x => x.cat === cat)?.subs || [];
            return (
              <div key={cat} className="cat-card">
                <div className="cat-head">
                  <span>{optLabel(cat)}</span>
                  <span className="cat-x" onClick={() => removeCategory(cat)} role="button" aria-label="Remove">×</span>
                </div>
                {subs.length > 0 && (
                  <div className="sub-list">
                    {subs.map(s => (
                      <label key={s} className="sub-row">
                        <input type="checkbox" checked={f.skills.includes(s)} onChange={() => toggleSkill(s)} />
                        <span>{optLabel(s)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <input type="text" value={f.expertiseOther} onChange={e => set({ expertiseOther: e.target.value })} placeholder={t('app.otherOptional')} style={{ marginTop: 6 }} />
        </div>

        <YesNo label={t('app.ownTools')} required value={f.hasTools} onChange={v => set({ hasTools: v })} />
        <YesNo label={t('app.licensedQ')} required value={f.licensed} onChange={v => set({ licensed: v })} />
        <YesNo label={t('app.insuredQ')} required value={f.insured} onChange={v => set({ insured: v })} />

        <div className="form-group">
          <label>{t('app.uploadDocs')} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>{t('app.upTo5')}</span></label>
          <input type="file" accept="image/*,.pdf" multiple onChange={e => {
            const picked = Array.from(e.target.files);
            setVendorDocFiles(prev => [...prev, ...picked].slice(0, 5));
            e.target.value = '';
          }} />
          {vendorDocFiles.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {vendorDocFiles.map((file, i) => (
                <span key={i} className="file-chip">{file.name}<span style={{ marginLeft: 6, cursor: 'pointer', fontWeight: 700 }} onClick={() => setVendorDocFiles(prev => prev.filter((_, idx) => idx !== i))}>×</span></span>
              ))}
            </div>
          )}
        </div>

        <YesNo label={t('app.companyQ')} value={f.hasCompany} onChange={v => set({ hasCompany: v })} />
        {f.hasCompany === 'Yes' && (
          <div className="form-group"><label>{t('app.companyLink')}</label><input type="text" value={f.companyLink} onChange={e => set({ companyLink: e.target.value })} placeholder="https://…" /></div>
        )}
        <YesNo label={t('app.crewQ')} value={f.hasCrew} onChange={v => set({ hasCrew: v })} />

        <div className="form-group"><label>{t('app.hourlyQ')}</label><input type="text" value={f.hourlyRate} onChange={e => set({ hourlyRate: e.target.value })} placeholder={t('app.hourlyPh')} /></div>
        <div className="form-group"><label>{t('app.sqftQ')} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>{t('app.sqftHint')}</span></label><input type="text" value={f.sqftRate} onChange={e => set({ sqftRate: e.target.value })} placeholder="e.g. $4/sqft LVP, $8/sqft tile" /></div>

        <button className="btn btn-primary btn-lg" disabled={isSubmittingApp} onClick={handleSubmitVendorApp}>
          {isSubmittingApp ? t('req.submitting') : t('app.submit')}
        </button>
      </div>
    </div>
  );
}

// ── Estimate detail sheet ───────────────────────────────────────────────────
// Tapping an estimate row opens this. Read-only: the tech's own price, broken
// out by line, with QPS's markup and the homeowner price deliberately absent
// (the server strips them before the estimate ever reaches this portal).
function EstimateSheet({ estimate, onClose }) {
  const display = useDisplayOptions();
  const [displayOpen, setDisplayOpen] = useState(false);
  const { t, lang } = useI18n();
  if (!estimate) return null;
  const e = estimate;
  const chip = estimateChip(e.status, t);
  // Reading this in the language it was written in shows the tech's own words;
  // any other language shows the English QPS works from.
  const txt = (field) => localized(e, field, lang);

  return (
    <>
      <div className="modal active">
        <div className="modal-box">
          <div className="modal-handle"></div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22 }}>
              {txt('trade') || t('tp.untitled')}
            </div>
            <span className={`tp-chip tp-chip-${chip.tone}`}>{chip.label}</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18, lineHeight: 1.5 }}>
            {e.estimateNumber ? `${e.estimateNumber} · ` : ''}{fmtLongDate(e.createdAt || e.submittedAt)}
            {e.serviceAddress ? <><br />{e.serviceAddress}</> : null}
          </div>

          {isTranslated(e, lang) && (
            <div style={{ background: 'var(--blue-light)', color: 'var(--blue-dark)', borderRadius: 10, padding: '9px 12px', fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>
              {t('tp.translatedFrom')} {sourceLangName(e)}. {t('tp.switchToSee')}
            </div>
          )}

          {/* The same Display Options Julian has on a printed document — the
              tech reads this sheet in front of a customer as often as he does,
              and a rate on screen is a conversation about the rate. */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setDisplayOpen(true)}>{t('disp.open')}</button>
          </div>
          <DisplayOptions open={displayOpen} onClose={() => setDisplayOpen(false)} />

          {(e.items || []).map((it, i) => {
            // "3 × $85" only reads as a sum when both halves are shown; with
            // one switched off it becomes the one number that is left.
            const workings = [
              display.qty ? String(it.qty) : null,
              display.rate ? fmtMoney(it.unitCost) : null,
            ].filter(Boolean).join(' × ');
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14 }}>{localized(it, 'description', lang, e.sourceLang)}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    {it.kind === 'material' ? t('pj.materials') : t('pj.labor')}{workings ? ` · ${workings}` : ''}
                  </div>
                </div>
                {display.itemTotals && <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>{fmtMoney(it.total)}</div>}
              </div>
            );
          })}

          <div style={{ display: 'flex', gap: 10, margin: '16px 0' }}>
            {[[t('pj.materials'), e.materialsTotal], [t('pj.labor'), e.laborTotal]].map(([label, value]) => (
              <div key={label} className="tp-tot-cell" style={{ flex: 1 }}>
                <div className="tp-tot-l">{label}</div>
                <div className="tp-tot-v">{fmtMoney(value)}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid var(--border)', fontSize: 17, marginBottom: 16 }}>
            <span>{t('pj.yourEstimateTotal')}</span>
            <span>{fmtMoney(e.techTotal)}</span>
          </div>

          {txt('notes') && (
            <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.6, marginBottom: 16 }}>{txt('notes')}</div>
          )}
          {e.status === 'rejected' && e.rejectedReason && (
            <div style={{ background: 'var(--red-light)', color: 'var(--red)', borderRadius: 10, padding: '11px 13px', fontSize: 13, lineHeight: 1.5, marginBottom: 16 }}>
              {e.rejectedReason}
            </div>
          )}

          <button className="btn btn-ghost btn-lg" onClick={onClose}>{t('req.cancel')}</button>
        </div>
      </div>
      <div className="overlay active" onClick={onClose}></div>
    </>
  );
}

// ── Invoice detail sheet ────────────────────────────────────────────────────
// Reuses LedgerCard so the row a tech opens here is exactly the row the admin
// sees on their side — payments, balance, progress reports and all.
function InvoiceSheet({ entry, onClose, onAddProgress }) {
  const { t } = useI18n();
  if (!entry) return null;
  return (
    <>
      <div className="modal active">
        <div className="modal-box">
          <div className="modal-handle"></div>
          <LedgerCard entry={entry} role="vendor" onAddProgress={onAddProgress} />
          <button className="btn btn-ghost btn-lg" style={{ marginTop: 14 }} onClick={onClose}>{t('req.cancel')}</button>
        </div>
      </div>
      <div className="overlay active" onClick={onClose}></div>
    </>
  );
}

// Estimate status → the chip shown on a row. QPS approving a tech's price is
// what Joist calls Approved/Declined, so the words line up with the tabs.
function estimateChip(status, t) {
  if (status === 'approved') return { tone: 'approved', label: t('tp.approved') };
  if (status === 'rejected') return { tone: 'declined', label: t('tp.declined') };
  if (status === 'draft') return { tone: 'draft', label: t('tp.draft') };
  return { tone: 'pending', label: t('tp.pending') };
}

// Invoice money state → chip. Derived from what's actually been paid rather
// than the stored status, so a part-paid invoice always reads as part-paid.
function invoiceChip(entry, t) {
  const paid = paidTotal(entry);
  const amount = Number(entry.invoiceAmount) || 0;
  if (amount > 0 && paid >= amount) return { tone: 'paid', label: t('tp.paid') };
  if (paid > 0) return { tone: 'partial', label: t('tp.partial') };
  return { tone: 'pending', label: t('tp.unpaid') };
}

export default function VendorDashboard() {
  const {
    currentScreen, goTo, currentUser, logout, vendorTab, setVendorTab, requests,
    setActiveRequest, setModal, vendorProfile,
    vendorView, setVendorView, ledger, openLedgerForm, openProgress,
    openCredential, handleDeleteCredential,
    projects, estimates, openEstimateForm,
  } = useApp();
  const { t, lang } = useI18n();
  const needsApplication = vendorProfile && !vendorProfile.profile?.completedAt;
  const compliance = vendorProfile?.compliance;

  // Which record the tech tapped into, if any.
  const [openEstimate, setOpenEstimate] = useState(null);
  const [openInvoice, setOpenInvoice] = useState(null);
  // Sub-filters for the two segmented list screens.
  const [estSeg, setEstSeg] = useState('pending');
  const [invSeg, setInvSeg] = useState('unpaid');

  const jobsFor = (tab) => requests.filter(r => {
    if (tab === 'active') return ['assigned', 'in-progress'].includes(r.status);
    if (tab === 'completed') return ['completed', 'invoiced'].includes(r.status);
    return r.status === 'paid';
  });
  const jobs = jobsFor(vendorTab);

  // ── Rows for the three list screens ──────────────────────────────────────

  const estimateRows = useMemo(() => (estimates || []).map(e => {
    const trade = localized(e, 'trade', lang);
    return {
    id: e._id,
    name: e.serviceAddress || trade || t('tp.untitled'),
    meta: e.serviceAddress && trade ? trade : '',
    number: e.estimateNumber,
    date: e.createdAt || e.submittedAt,
    amount: e.techTotal,
    status: e.status,
    ...(({ tone, label }) => ({ chipTone: tone, chipLabel: label }))(estimateChip(e.status, t)),
    onClick: () => setOpenEstimate(e),
    };
  }), [estimates, t, lang]);

  const invoiceRows = useMemo(() => (ledger || []).map(e => ({
    id: e._id,
    name: e.serviceAddress || localized(e, 'jobDetails', lang) || t('tp.untitled'),
    meta: '',
    number: e.invoiceNumber,
    date: e.createdAt,
    amount: e.invoiceAmount,
    entry: e,
    ...(({ tone, label }) => ({ chipTone: tone, chipLabel: label }))(invoiceChip(e, t)),
    onClick: () => setOpenInvoice(e),
  })), [ledger, t, lang]);

  // Every payment across every invoice, flattened into one feed.
  const paymentRows = useMemo(() => (ledger || []).flatMap(e =>
    (e.payments || []).map(p => ({
      id: `${e._id}-${p._id}`,
      name: e.serviceAddress || localized(e, 'jobDetails', lang) || t('tp.untitled'),
      meta: [p.mode, p.reference].filter(Boolean).join(' · '),
      number: e.invoiceNumber,
      date: p.date || p.recordedAt,
      amount: p.amount,
      chipTone: 'paid',
      chipLabel: t('tp.received'),
      onClick: () => setOpenInvoice(e),
    }))
  ), [ledger, t, lang]);

  const totals = ledgerTotals(ledger || []);

  const TABS = [
    { key: 'jobs', label: t('tp.jobs'), Icon: IconJobs },
    { key: 'estimates', label: t('tp.estimates'), Icon: IconEstimates },
    { key: 'invoices', label: t('tp.invoices'), Icon: IconInvoices },
    { key: 'payments', label: t('tp.payments'), Icon: IconPayments },
    { key: 'more', label: t('tp.more'), Icon: IconMore, dot: compliance?.status === 'blocked' || compliance?.status === 'expiring' },
  ];

  return (
    <div className={`screen ${currentScreen === 'screen-vendor-dashboard' ? 'active' : ''}`}>
      <nav className="nav">
        <div className="nav-brand">
          <button type="button" className="brandmark" aria-label="Home" onClick={() => goTo('screen-home')}><Seal size={30} /></button>
          {/* Camel on forest — the emerald pill this replaced was left over
              from the pre-Olive & Oat palette. */}
          <span style={{ fontSize: 11, background: 'rgba(198,176,138,.16)', color: 'var(--nav-accent)', padding: '2px 9px', borderRadius: 50, marginLeft: 6, letterSpacing: '.14em' }}>Tech</span>
        </div>
        <div className="nav-right" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'var(--border)', fontSize: 13 }}>{currentUser?.name}</span>
          <LanguageSwitcher dark />
        </div>
      </nav>

      {needsApplication ? <VendorApplication /> : (
      <>
      <div className="tp-body">

        {/* ── Jobs ─────────────────────────────────────────────── */}
        {vendorView === 'jobs' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <div style={{ width: 34 }} />
                <div className="tp-title">{t('tp.jobs')}</div>
                <div style={{ width: 34 }} />
              </div>
              <div className="tp-seg" role="tablist">
                {['active', 'completed', 'paid'].map(tab => (
                  <button key={tab} role="tab" aria-selected={vendorTab === tab}
                    className={vendorTab === tab ? 'active' : ''} onClick={() => setVendorTab(tab)}>
                    {t('vd.' + tab)}
                    {jobsFor(tab).length > 0 && <span className="tp-seg-n">{jobsFor(tab).length}</span>}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ padding: '16px 16px 0' }}>
              {compliance?.status === 'blocked' && <ComplianceBanner compliance={compliance} forVendor />}
              {jobs.map(req => (
                <div key={req._id} className="card" style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 8 }}>
                    <div style={{ fontSize: 15 }}>{tr('opt.' + req.serviceType, req.serviceType)}</div>
                    {statusTag(req.status)}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>{req.homeownerId?.name || req.fullName}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{req.serviceAddress}</div>
                  {/* A commercial job carries things a homeowner job never
                      does, and all of them decide whether this visit works:
                      whether it's an emergency, who to ring to be let in, and
                      the move-out inspection the work is being quoted against.
                      A document nobody on site can open is the same as no
                      document, which is why it is HERE and not only in the
                      manager's portal. */}
                  {req.priority && req.priority !== 'routine' && (
                    <div style={{ marginBottom: 8 }}>
                      <span className={`tag tag-${req.priority === 'emergency' ? 'red' : 'amber'}`}>
                        {tr('acct.priority.' + req.priority, req.priority)}
                      </span>
                    </div>
                  )}
                  {(req.access?.tenantName || req.access?.lockboxCode || req.access?.gateCode || req.access?.pets) && (
                    <div style={{ background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '9px 11px', marginBottom: 8, fontSize: 12.5, lineHeight: 1.7 }}>
                      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--muted)', marginBottom: 2 }}>
                        {tr('acct.gettingIn', 'Getting in')}
                      </div>
                      {req.access.tenantName && (
                        <div>
                          {req.access.tenantName}
                          {req.access.tenantPhone && <> · <a href={`tel:${req.access.tenantPhone}`} style={{ color: 'var(--blue)', textDecoration: 'none' }}>{req.access.tenantPhone}</a></>}
                        </div>
                      )}
                      {req.access.lockboxCode && <div>{tr('acct.lockboxCode', 'Lockbox')}: {req.access.lockboxCode}</div>}
                      {req.access.gateCode && <div>{tr('acct.gateCode', 'Gate')}: {req.access.gateCode}</div>}
                      {req.access.pets && <div>{tr('acct.pets', 'Pets')}: {req.access.pets}</div>}
                      {req.access.notes && <div>{req.access.notes}</div>}
                    </div>
                  )}
                  {(req.documents || []).length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      {req.documents.map((d, i) => (
                        <a key={d._id || i} className="btn btn-outline btn-sm" href={d.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                          {d.name || tr('doc.file', 'File')}
                        </a>
                      ))}
                    </div>
                  )}
                  {/* Everything the customer asked for, not just the trade the
                      job is filed under. A visit booked as "Plumbing" that is
                      really a leak, a gate and a hole in the wall is three jobs
                      the tech should know about before they load the van. */}
                  {req.items?.length > 1 && (
                    <div style={{ marginBottom: 8 }}>
                      {req.items.map((it, i) => (
                        <div key={i} style={{ fontSize: 13, color: 'var(--slate)', padding: '2px 0' }}>
                          <span style={{ color: 'var(--muted)' }}>{i + 1}. </span>
                          {it.title || tr('opt.' + it.serviceType, it.serviceType)}
                          {it.photos?.length > 0 && <span style={{ color: 'var(--muted)' }}> · {it.photos.length}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {req.estimate && <div style={{ fontSize: 13, color: 'var(--green)', marginBottom: 10 }}>{t('vd.estimate')} {fmtMoney(req.estimate.amount)}</div>}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <MessageButton request={req} />
                    {req.status === 'assigned' && (
                      <button className="btn btn-primary btn-sm" onClick={() => { setActiveRequest(req); setModal('completion'); }}>{t('vd.submitCompletionPhotos')}</button>
                    )}
                    {req.status === 'completed' && (
                      <button className="btn btn-green btn-sm" onClick={() => { setActiveRequest(req); setModal('invoice'); }}>{t('vd.submitInvoice')}</button>
                    )}
                    {req.status === 'invoiced' && (
                      <span style={{ fontSize: 12, color: 'var(--amber)' }}>{t('vd.invoicePending')}</span>
                    )}
                    {req.status === 'paid' && (
                      <span style={{ fontSize: 12, color: 'var(--green)' }}>{t('vd.paidDash')} {fmtMoney(req.invoice?.amount)}</span>
                    )}
                  </div>
                </div>
              ))}
              {jobs.length === 0 && (
                <div className="tp-empty">
                  <div className="tp-empty-t">{t('tp.noJobsT')}</div>
                  <div>{t('vd.noJobs')}</div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Estimates ────────────────────────────────────────── */}
        {vendorView === 'estimates' && (
          <RecordList
            title={t('tp.estimates')}
            rows={estimateRows}
            segments={[
              { key: 'pending', label: t('tp.pending'), match: r => r.status === 'submitted' || r.status === 'draft' },
              { key: 'approved', label: t('tp.approved'), match: r => r.status === 'approved' },
              { key: 'declined', label: t('tp.declined'), match: r => r.status === 'rejected' },
            ]}
            segment={estSeg}
            onSegment={setEstSeg}
            searchPlaceholder={t('tp.searchEstimates')}
            onAdd={openEstimateForm}
            addLabel={t('pj.newEstimate')}
            emptyTitle={t('tp.noEstimates')}
            emptyText={t('tp.noEstimatesSub')}
          />
        )}

        {/* ── Invoices ─────────────────────────────────────────── */}
        {vendorView === 'invoices' && (
          <>
            {compliance?.status === 'blocked' && (
              <div style={{ padding: '14px 16px 0' }}><ComplianceBanner compliance={compliance} forVendor /></div>
            )}
            <RecordList
              title={t('tp.invoices')}
              rows={invoiceRows}
              segments={[
                { key: 'unpaid', label: t('tp.unpaid'), match: r => paidTotal(r.entry) === 0 },
                { key: 'partial', label: t('tp.partial'), match: r => paidTotal(r.entry) > 0 && balanceOf(r.entry) > 0 },
                { key: 'paid', label: t('tp.paid'), match: r => (Number(r.entry.invoiceAmount) || 0) > 0 && balanceOf(r.entry) <= 0 },
              ]}
              segment={invSeg}
              onSegment={setInvSeg}
              searchPlaceholder={t('tp.searchInvoices')}
              onAdd={() => openLedgerForm(null)}
              addLabel={t('lg.submitInvoice')}
              totals={[
                [t('lg.totalInvoiced'), totals.invoiced],
                [t('lg.totalPaid'), totals.paid],
                [t('lg.totalOwed'), totals.owed],
              ]}
              emptyTitle={t('tp.noInvoicesT')}
              emptyText={t('tp.noInvoicesSub')}
            />
          </>
        )}

        {/* ── Payments ─────────────────────────────────────────── */}
        {vendorView === 'payments' && (
          <RecordList
            title={t('tp.payments')}
            rows={paymentRows}
            searchPlaceholder={t('tp.searchPayments')}
            totals={[
              [t('lg.totalPaid'), totals.paid],
              [t('lg.totalOwed'), totals.owed],
            ]}
            emptyTitle={t('tp.noPayments')}
            emptyText={t('tp.noPaymentsSub')}
          />
        )}

        {/* ── More ─────────────────────────────────────────────── */}
        {vendorView === 'more' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <div style={{ width: 34 }} />
                <div className="tp-title">{t('tp.more')}</div>
                <div style={{ width: 34 }} />
              </div>
            </div>

            <div style={{ padding: '0 14px' }}><InstallCard /></div>

            <div className="tp-more-g">
              <div className="tp-more-h">{t('tp.workGroup')}</div>
              <button className="tp-more-row" onClick={() => setVendorView('docs')}>
                <span>
                  {t('lg.tabDocs')}
                  {compliance?.status === 'blocked' && <span style={{ color: 'var(--red)' }}> ●</span>}
                  {compliance?.status === 'expiring' && <span style={{ color: 'var(--amber)' }}> ●</span>}
                </span>
                <span className="tp-more-chev"><IconChevron /></span>
              </button>
              <button className="tp-more-row" onClick={() => setVendorView('settlements')}>
                <span>{t('tp.settlements')}</span>
                <span className="tp-more-chev"><IconChevron /></span>
              </button>
            </div>

            <div className="tp-more-g">
              <div className="tp-more-h">{t('tp.account')}</div>
              <div className="tp-more-row" style={{ cursor: 'default' }}>
                <span style={{ color: 'var(--muted)', fontSize: 14 }}>{currentUser?.name}</span>
              </div>
              <div className="tp-more-row" style={{ cursor: 'default' }}>
                <span>{t('tp.language')}</span>
                <LanguageSwitcher />
              </div>
              <button className="tp-more-row" onClick={logout}>
                <span style={{ color: 'var(--red)' }}>{t('nav.logout')}</span>
                <span className="tp-more-chev"><IconChevron /></span>
              </button>
            </div>
          </>
        )}

        {/* ── My Documents (reached from More) ─────────────────── */}
        {vendorView === 'docs' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <button className="tp-act" onClick={() => setVendorView('more')} aria-label={t('nav.back')}
                  style={{ transform: 'scaleX(-1)' }}><IconChevron size={20} /></button>
                <div className="tp-title">{t('lg.tabDocs')}</div>
                <div style={{ width: 34 }} />
              </div>
            </div>
            <div style={{ padding: '16px 16px 0' }}>
              <ComplianceBanner compliance={compliance} forVendor />
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 }}>{t('lg.docsIntro')}</div>
              <CompliancePanel
                compliance={compliance}
                onUpload={(item) => openCredential(item)}
                onDelete={(item) => handleDeleteCredential(item)}
              />
            </div>
          </>
        )}

        {/* ── Project settlements (reached from More) ──────────── */}
        {vendorView === 'settlements' && (
          <>
            <div className="tp-head">
              <div className="tp-head-row">
                <button className="tp-act" onClick={() => setVendorView('more')} aria-label={t('nav.back')}
                  style={{ transform: 'scaleX(-1)' }}><IconChevron size={20} /></button>
                <div className="tp-title">{t('tp.settlements')}</div>
                <div style={{ width: 34 }} />
              </div>
            </div>
            <div style={{ padding: '16px 16px 0' }}>
              {(projects || []).length === 0 ? (
                <div className="tp-empty">
                  <div className="tp-empty-t">{t('tp.settlements')}</div>
                  <div>{t('pj.noSettlements')}</div>
                </div>
              ) : projects.map(s2 => <TechSettlement key={s2.projectId} settlement={s2} />)}
            </div>
          </>
        )}
      </div>

      {/* ── Bottom tab bar ─────────────────────────────────────── */}
      <div className="tp-bar" role="tablist">
        {TABS.map(({ key, label, Icon, dot }) => {
          // "Documents" and "Settlements" are pushed from More, so More stays
          // lit while the tech is inside either of them.
          const active = vendorView === key
            || (key === 'more' && ['docs', 'settlements'].includes(vendorView));
          return (
            <button key={key} role="tab" aria-selected={active} className={active ? 'active' : ''}
              onClick={() => setVendorView(key)}>
              <Icon size={21} />
              <span className="tp-bar-l">{label}</span>
              {dot && <span className="tp-bar-dot" />}
            </button>
          );
        })}
      </div>

      <EstimateSheet estimate={openEstimate} onClose={() => setOpenEstimate(null)} />
      <InvoiceSheet entry={openInvoice} onClose={() => setOpenInvoice(null)} onAddProgress={openProgress} />
      </>
      )}
    </div>
  );
}
